import React, { useMemo } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import {
  defaultQuotationsListSort,
  emptyQuotationsListFilters,
} from '../../../../api/strapiCrm';
import { useAuth } from '../../../../auth/AuthContext';
import { useCustomersQuery, useQuotationsListQuery, useUsersQuery } from '../../../../query/hooks';
import { isMarketingPic, resolveQuotationWorkflowUser } from '../../../QuotationsPage/quotationWorkflow';

/** Ringkasan jumlah customer & quotation. */
export function MarketingRingkasanTabScreen() {
  const { jwt, user } = useAuth();
  const isPic = isMarketingPic(user);

  const selfUserQ = useUsersQuery(
    jwt,
    {
      emailContains: user?.email?.trim() ?? '',
      status: 'active',
    },
    Boolean(jwt) && isPic && Boolean(user?.email?.trim()),
  );
  const workflowUser = useMemo(
    () => resolveQuotationWorkflowUser(user, selfUserQ.data?.rows),
    [user, selfUserQ.data?.rows],
  );
  const picAssignee = useMemo(() => {
    if (!isPic) return undefined;
    const u = workflowUser ?? user;
    if (!u) return undefined;
    return { documentId: u.documentId, id: u.id };
  }, [isPic, workflowUser, user]);

  const customersQ = useCustomersQuery(jwt);
  const quotationsQ = useQuotationsListQuery(
    jwt,
    emptyQuotationsListFilters,
    defaultQuotationsListSort,
    1,
    1,
    {
      visibility: isPic ? 'marketing_pic' : 'all',
      picAssignee,
    },
  );

  const nCustomers = customersQ.data?.total ?? 0;
  const nQuotations = quotationsQ.data?.total ?? 0;
  const loading = customersQ.isPending || quotationsQ.isPending;
  const err = customersQ.error || quotationsQ.error;

  if (err) {
    return (
      <Typography color="error" variant="body2">
        {err instanceof Error ? err.message : 'Gagal memuat ringkasan.'}
      </Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          width: '100%',
          minWidth: 0,
        }}
      >
        <Card variant="outlined" sx={{ minWidth: 0 }}>
          <CardContent>
            <Typography color="text.secondary" variant="overline">
              Total customer
            </Typography>
            <Typography variant="h4" component="p">
              {loading ? '…' : nCustomers}
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ minWidth: 0 }}>
          <CardContent>
            <Typography color="text.secondary" variant="overline">
              Total quotation
            </Typography>
            <Typography variant="h4" component="p" data-testid="dashboard-total-quotation">
              {loading ? '…' : nQuotations}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
